import { useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Accordion - Reusable accordion/FAQ component
 * 
 * Features:
 * - Expand/collapse animation
 * - Only one item open at a time
 * - Icon rotation on expand
 * - Keyboard accessible
 * 
 * Used for: FAQ page, collapsible content sections
 */
const Accordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const isOpen = openIndex === index;

        return (
          <div
            key={item.id || index}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Question Button */}
            <button
              onClick={() => toggleItem(index)}
              className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors duration-200"
              aria-expanded={isOpen}
              aria-controls={`accordion-content-${index}`}
            >
              <h3 className="text-lg font-semibold text-gray-900 pr-4">
                {item.question}
              </h3>
              
              {/* Arrow Icon */}
              <svg
                className={`w-6 h-6 text-primary-500 flex-shrink-0 transition-transform duration-300 ${
                  isOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Answer Content */}
            <div
              id={`accordion-content-${index}`}
              className={`transition-all duration-300 ease-in-out ${
                isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              } overflow-hidden`}
            >
              <div className="p-6 pt-0 text-gray-600 text-base leading-relaxed">
                {item.answer}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

Accordion.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default Accordion;

